import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateReseller } from "@/api/reseller-manager/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createResellerSchema } from "@/lib/schemas/reseller.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type CreateResellerDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

export function CreateResellerDialog({
	open,
	onOpenChange,
}: CreateResellerDialogProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const create = useCreateReseller();

	useEffect(() => {
		if (open) {
			setEmail("");
			setCompanyName("");
			setFieldErrors({});
		}
	}, [open]);

	function handleSubmit() {
		setFieldErrors({});
		const result = createResellerSchema.safeParse({
			email: email.trim(),
			company_name: companyName.trim(),
		});
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		create.mutate(
			{ email: email.trim(), company_name: companyName.trim() },
			{
				onSuccess: () => {
					toast.success(t("resellers.created"));
					onOpenChange(false);
				},
				onError: (error) => {
					if (error.message === "rate_limited") return;
					const drfErrors = getDrfFieldErrors(error);
					setFieldErrors(drfErrors);
					toast.error(t("resellers.createError"));
				},
			},
		);
	}

	const canSubmit =
		email.trim() &&
		companyName.trim() &&
		!create.isPending &&
		!create.isRateLimited;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Utwórz resellera</DialogTitle>
					<DialogDescription>Podaj dane nowego resellera</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="reseller-email">E-mail</Label>
						<Input
							id="reseller-email"
							type="email"
							name="email"
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="np. jan@firma.pl"
							aria-invalid={!!fieldErrors.email}
							aria-describedby={
								fieldErrors.email ? "reseller-email-error" : undefined
							}
						/>
						{fieldErrors.email && (
							<p
								id="reseller-email-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.email}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="reseller-company">Nazwa firmy</Label>
						<Input
							id="reseller-company"
							name="company"
							autoComplete="organization"
							value={companyName}
							onChange={(e) => setCompanyName(e.target.value)}
							placeholder="np. Firma Sp. z o.o."
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.company_name}
							aria-describedby={
								fieldErrors.company_name ? "reseller-company-error" : undefined
							}
						/>
						{fieldErrors.company_name && (
							<p
								id="reseller-company-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.company_name}
							</p>
						)}
					</div>
				</div>
				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={create.isPending}
					>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{create.isPending ? "Tworzenie…" : "Utwórz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
